import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import { getInterestIcon } from "@/components/mentor-profile/interest-icons";
import {
  useUploadMentorImage,
  type MentorProfileDraft,
} from "@/lib/api/mentor-profile";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import {
  INTEREST_MAX_SELECTIONS,
  INTEREST_MIN_PHOTOS,
  INTEREST_MIN_SELECTIONS,
  INTEREST_PHOTO_TIPS,
  MENTOR_INTEREST_OPTIONS,
} from "@/lib/mentor-profile/constants";
import {
  selectableInterests,
  splitInterestPhotos,
  type InterestImage,
} from "@/lib/mentor-profile/interest-photos";
import { uploadVerticalMentorImage } from "@/lib/mentor-profile/upload-vertical-image";
import { cn } from "@/lib/utils";
import { Moodies } from "@somethings-ui/core/assets";
import { IconButton } from "@somethings-ui/web";
import { Check, ImagePlus, Loader2, Sparkles, X } from "lucide-react";
import { useRef, useState } from "react";
import { WizardStepLayout } from "./wizard-step-layout";
import type { WizardStepProps } from "./types";

function InterestPhotoSlot({
  interest,
  imageUrl,
  uploading,
  legacy,
  onUpload,
  onClear,
}: {
  interest: string;
  imageUrl?: string;
  uploading?: boolean;
  legacy?: boolean;
  onUpload?: (file: File) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { url: signedUrl, isPending: isSigning } = useSignedMedia(imageUrl);
  const previewUrl = signedUrl ?? undefined;
  const Icon = getInterestIcon(interest);
  const hasPhoto = !!imageUrl;

  return (
    <div
      className={cn(
        "relative aspect-[294/392] w-full overflow-hidden rounded-[26.4px] border-4 border-white shadow-[0_14px_34px_rgba(31,30,29,0.2)]",
        hasPhoto
          ? "bg-gradient-to-b from-[#e3f9ff] to-[#ffead0] p-1"
          : "bg-gradient-to-b from-[rgba(177,100,255,0.15)] to-[rgba(113,224,255,0.2)]",
        uploading && "opacity-70",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload?.(file);
          e.target.value = "";
        }}
      />

      {hasPhoto ? (
        <>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt=""
              className="size-full rounded-[18px] object-cover"
            />
          ) : (
            <div className="flex size-full items-center justify-center rounded-[18px] bg-[#f5f3f0]/40">
              <Loader2
                className={cn(
                  "size-5 text-[#797774]",
                  isSigning && "animate-spin",
                )}
              />
            </div>
          )}
          <IconButton
            size="sm"
            variant="glass"
            className="absolute right-2.5 top-2.5 z-10 bg-black/55 hover:bg-black/70"
            aria-label={`Remove ${interest} photo`}
            icon={<X className="size-4" strokeWidth={1.75} />}
            onClick={onClear}
          />
          <div className="pointer-events-none absolute left-2.5 top-2.5 z-10 inline-flex h-8 max-w-[calc(100%-3.75rem)] items-center gap-1.5 rounded-full border border-white/60 bg-black/35 px-3 py-1.5">
            <Icon className="size-4 shrink-0 text-white" strokeWidth={1.75} />
            <span className="truncate font-body text-sm font-medium leading-5 text-white">
              {interest}
            </span>
          </div>
          {legacy ? (
            <span className="pointer-events-none absolute bottom-2.5 left-2.5 z-10 rounded-full bg-black/55 px-2.5 py-1 font-body text-xs font-medium uppercase tracking-[0.3px] text-white">
              Legacy
            </span>
          ) : null}
        </>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex size-full flex-col items-center justify-center gap-3 px-6 text-center transition-opacity hover:opacity-95"
        >
          <span className="flex size-16 shrink-0 items-center justify-center rounded-full bg-white shadow-[0_1px_1.5px_rgba(0,0,0,0.1),0_1px_1px_rgba(0,0,0,0.1)]">
            {uploading ? (
              <Loader2 className="size-7 animate-spin text-text-primary" />
            ) : (
              <ImagePlus className="size-7 text-text-primary" strokeWidth={1.75} />
            )}
          </span>
          <span className="font-body text-sm leading-5 text-[rgba(31,30,29,0.8)]">
            Add a photo of you doing{" "}
            <span className="font-semibold text-[#1f1e1d]">{interest}</span>
          </span>
        </button>
      )}
    </div>
  );
}

export function InterestsStep({
  draft,
  saveState,
  setFields,
  embedded,
  reviewerNotes,
  onContinue,
  onBack,
}: WizardStepProps) {
  const upload = useUploadMentorImage();
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [uploadingInterests, setUploadingInterests] = useState<Set<string>>(
    () => new Set(),
  );
  const [error, setError] = useState<string | null>(null);

  const selected = selectableInterests(draft.interests);
  const images = draft.interest_images ?? [];
  const { photos, legacy } = splitInterestPhotos(selected, images);
  const photoCount = photos.length;
  const canContinue =
    selected.length >= INTEREST_MIN_SELECTIONS &&
    photoCount >= INTEREST_MIN_PHOTOS &&
    uploadingInterests.size === 0;

  const commit = (patch: Partial<MentorProfileDraft>) => {
    draftRef.current = { ...draftRef.current, ...patch };
    setFields(patch);
  };

  const commitImages = (nextImages: InterestImage[]) =>
    commit({ interest_images: nextImages });

  const commitSelection = (
    nextSelected: string[],
    nextImages: InterestImage[],
  ) => commit({ interests: nextSelected, interest_images: nextImages });

  const toggleInterest = (interest: string) => {
    setError(null);
    const isSelected = selected.includes(interest);

    if (isSelected) {
      commitSelection(
        selected.filter((item) => item !== interest),
        images.filter((image) => image.interest !== interest),
      );
      return;
    }

    if (selected.length >= INTEREST_MAX_SELECTIONS) {
      setError(`You can pick up to ${INTEREST_MAX_SELECTIONS} interests.`);
      return;
    }

    commitSelection([...selected, interest], images);
  };

  const clearPhoto = (index: number | undefined) => {
    setError(null);
    commitImages(images.filter((_, i) => i !== index));
  };

  const handleUpload = async (interest: string, file: File) => {
    setError(null);
    setUploadingInterests((prev) => new Set(prev).add(interest));

    const result = await uploadVerticalMentorImage(file, {
      filename:
        file.name || `${interest.toLowerCase().replace(/\s+/g, "-")}.jpg`,
      upload: (params) => upload.mutateAsync(params),
    });

    setUploadingInterests((prev) => {
      const next = new Set(prev);
      next.delete(interest);
      return next;
    });

    if (!result.ok) {
      setError(result.error);
      return;
    }

    const latest = draftRef.current;
    const latestImages = latest.interest_images ?? [];
    const replaceIndex = splitInterestPhotos(
      selectableInterests(latest.interests),
      latestImages,
    ).photos.find((photo) => photo.interest === interest)?.index;
    const uploaded = { interest, imageUrl: result.url };
    commitImages(
      replaceIndex === undefined
        ? [...latestImages, uploaded]
        : latestImages.map((image, i) =>
            i === replaceIndex ? uploaded : image,
          ),
    );
  };

  const helperText = (() => {
    if (error) return null;
    if (selected.length < INTEREST_MIN_SELECTIONS) {
      return "Pick at least 3 interests.";
    }
    if (photoCount < INTEREST_MIN_PHOTOS) {
      return "Add a photo to at least 3 of them — it's what teens connect with.";
    }
    return `${photoCount}/${INTEREST_MIN_PHOTOS} photos · looking good`;
  })();

  return (
    <WizardStepLayout
      reviewerNotes={reviewerNotes}
      embedded={embedded}
      stepIndex={8}
      icon={
        <img
          src={Moodies.pleased}
          alt=""
          className="size-10 object-contain"
        />
      }
      heading="What are you into?"
      subheading="Pick a few, then add a photo of you actually doing each one. Teens love seeing your interests come to life."
      saveState={saveState}
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!canContinue}
      preview={
        <MatchingProfilePreviewCard
          draft={draft}
          focus={{ region: "interests", zoom: 1.05 }}
        />
      }
    >
      <div className="flex flex-wrap gap-2.5">
        {MENTOR_INTEREST_OPTIONS.map((interest) => {
          const isSelected = selected.includes(interest);
          const atMax =
            !isSelected && selected.length >= INTEREST_MAX_SELECTIONS;

          return (
            <button
              key={interest}
              type="button"
              onClick={() => toggleInterest(interest)}
              disabled={atMax}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-4 py-2 font-body text-sm transition-colors",
                isSelected
                  ? "border-text-primary bg-buttonBackground-primary text-buttonText-primary"
                  : "border-surfaces-z5 bg-surfaces-z0 text-text-primary hover:border-text-primary/40",
                atMax && "cursor-not-allowed opacity-40",
              )}
            >
              {isSelected ? <Check className="size-3.5" /> : null}
              {interest}
            </button>
          );
        })}
      </div>

      {selected.length > 0 ? (
        <div className="mt-2 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <p className="font-body text-xs font-medium uppercase tracking-[0.3px] text-text-secondary">
              Add a photo of you doing each
            </p>
            <p className="font-body text-xs font-medium text-text-secondary">
              {photoCount}/{INTEREST_MIN_PHOTOS} photos
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {selected.map((interest) => {
              const photo = photos.find((item) => item.interest === interest);
              return (
                <InterestPhotoSlot
                  key={interest}
                  interest={interest}
                  imageUrl={photo?.imageUrl}
                  uploading={uploadingInterests.has(interest)}
                  onUpload={(file) => void handleUpload(interest, file)}
                  onClear={() => clearPhoto(photo?.index)}
                />
              );
            })}
          </div>
        </div>
      ) : null}

      {error ? (
        <span className="font-body text-sm text-states-error">{error}</span>
      ) : helperText ? (
        <span className="font-body text-sm text-text-secondary">{helperText}</span>
      ) : null}

      {legacy.length > 0 ? (
        <div className="mt-2 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <p className="font-body text-xs font-medium uppercase tracking-[0.3px] text-text-secondary">
              Legacy photos
            </p>
            <p className="font-body text-sm text-text-secondary">
              These are from an older version of your profile and aren&apos;t
              tied to the interests above. Teens can still see them, so remove
              any you don&apos;t want shown.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {legacy.map((photo) => (
              <InterestPhotoSlot
                key={`${photo.index}-${photo.imageUrl}`}
                interest={photo.interest}
                imageUrl={photo.imageUrl}
                legacy
                onClear={() => clearPhoto(photo.index)}
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-1 rounded-lg bg-brand-ecru/60 px-3.5 py-3">
        <div className="flex items-center gap-1.5">
          <Sparkles className="size-3.5 text-brand-tangelo" />
          <p className="font-body text-xs font-medium text-text-primary/70">
            Tips for interest photos
          </p>
        </div>
        <ul className="mt-1.5 space-y-1">
          {INTEREST_PHOTO_TIPS.map((tip) => (
            <li key={tip} className="flex gap-2">
              <span className="mt-1.5 size-1 shrink-0 rounded-full bg-text-primary/40" />
              <span className="font-body text-body-3 leading-snug text-text-primary/80">
                {tip}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </WizardStepLayout>
  );
}
